import React from "react";
import { graphql } from "gatsby";
import {
  Box,
  Hidden,
  styled,
  Theme,
  Typography,
  useTheme
} from "@material-ui/core";
import fastCompare from "react-fast-compare";

const SquareWrapper = styled("div")(
  ({
    theme,
    image,
    color
  }: {
    theme: Theme;
    image: string;
    color: string;
  }) => ({
    maxWidth: "100vw",
    flex: 1,
    flexShrink: 0,
    aspectRatio: "1 / 1",
    "@supports not (aspect-ratio: auto)": {
      height: "450px"
    },
    backgroundImage: `url(${image})`,
    backgroundPosition: "top",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(4),
    textAlign: "center",
    color:
      color === "white"
        ? theme.palette.common.white
        : theme.palette.text.primary,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4)
    },
    [theme.breakpoints.down("xs")]: {
      flex: "unset",
      padding: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      aspectRatio: "0.72 / 1",
      "@supports not (aspect-ratio: auto)": {
        height: "500px"
      }
    }
  })
);

const JoinClubSquare = ({
  index,
  title,
  description,
  image,
  color
}: {
  index: number;
  title: string;
  description: string;
  image: string;
  color: "black" | "white";
}) => {
  return (
    <SquareWrapper image={image} color={color}>
      <Box mb={{ xs: 0.5 }}>
        <Typography className="compact">Step {index}</Typography>
      </Box>

      <Box mb={{ xs: 1.5 }}>
        {/* XL */}
        <Hidden implementation="css" lgDown>
          <Typography variant="h4">{title}</Typography>
        </Hidden>

        {/* Md/Lg */}
        <Hidden implementation="css" smDown xlUp>
          <Typography variant="h5">{title}</Typography>
        </Hidden>

        {/* Mobile */}
        <Hidden implementation="css" mdUp>
          <Typography variant="h3">{title}</Typography>
        </Hidden>
      </Box>
      <Box paddingX={{ xs: 0, sm: 4, md: 0, lg: 2, xl: 4 }}>
        <Typography
          variant="body1"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </Box>
    </SquareWrapper>
  );
};

function JoinClubSection({
  step1Title,
  step1Description,
  step1Image,
  step2Title,
  step2Description,
  step2Image,
  step3Title,
  step3Description,
  step3Image
}: Queries.JoinClubFragmentFragment) {
  const theme = useTheme();
  return (
    <Box display="flex" flexDirection={{ xs: "column", md: "row" }}>
      <JoinClubSquare
        index={1}
        title={step1Title}
        description={step1Description}
        image={step1Image.url}
        color="black"
      />
      <JoinClubSquare
        index={2}
        title={step2Title}
        description={step2Description}
        image={step2Image.url}
        color="white"
      />
      <JoinClubSquare
        index={3}
        title={step3Title}
        description={step3Description}
        image={step3Image.url}
        color="white"
      />
    </Box>
  );
}

export default React.memo(JoinClubSection, fastCompare);

export const JoinClubFragment = graphql`
  fragment JoinClubFragment on DatoCmsCModuleJoinTheClub {
    step1Title
    step1Description
    step1Image {
      url
    }
    step2Title
    step2Description
    step2Image {
      url
    }
    step3Title
    step3Description
    step3Image {
      url
    }
    container
    innerContainer
    containerDisableGutters
    containerBackgroundColor
    visibilitySettings {
      ...ContentModuleVisibilitySettingFragment
    }
    model {
      apiKey
    }
  }
`;
